import {
  Box,
  Container,
  Flex,
  Text,
  Heading,
  Grid,
  Spinner,
} from '@radix-ui/themes';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/fulltilt/contexts/use-tenant-context';
import { Card } from '@trashlab/sitesapp/src/organizations/fulltilt/template/components/shared/card';
import { useEffect, useRef, useState, useMemo } from 'react';
import { trpcWebsiteGetGoogleReviews } from '@trashlab/api/src/generated/hooks';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/fulltilt/lib/utils/get-variant-value';
import { asAddress } from '@trashlab/api/src/library/server/utils/json-compatibility';

type VariantValue = {
  value: string;
  orderIndex?: number;
};

type Stat = {
  finalValue: number;
  displayValue: string | undefined; // Custom display format (e.g., "50k+", "4.9")
  title: string;
  description: string;
};

export const CompanyStats = ({
  withBackground = false,
}: {
  withBackground?: boolean;
}) => {
  const { website } = useTenantContext();
  const orgAddress = asAddress(website?.organization?.address);
  const companyName = website?.organization?.name || 'our company';

  const titles = useVariantValue<VariantValue[]>('STATS_TITLE', []);
  const subtitles = useVariantValue<VariantValue[]>('STATS_SUBTITLE', []);
  const values = useVariantValue<VariantValue[]>('STATS_VALUE', []);

  const { data: googleData, isLoading: isGoogleLoading } =
    trpcWebsiteGetGoogleReviews.useQuery(
      {
        placeId: website?.googlePlaceId ?? '',
        limit: 4,
        minRating: 4,
      },
      {
        enabled: !!(website?.googlePlaceId && website?.enableGoogleReviews),
        staleTime: 60 * 60 * 1000,
      },
    );

  const defaultStatsTitle = `A trusted and renowned name in ${
    orgAddress?.city ?? 'San Francisco'
  }`;
  const defaultStatsSubtitle = 'Get your dumpster fast and hassle-free';

  const statsTitle = useVariantValue('COMPANY_STATS_TITLE', defaultStatsTitle);
  const statsSubtitle = useVariantValue(
    'COMPANY_STATS_SUBTITLE',
    defaultStatsSubtitle,
  );

  const defaultStats: Stat[] = [
    {
      finalValue: 35,
      displayValue: undefined,
      title: 'Service Areas',
      description: `${companyName} serves 35 locations to ensure prompt dumpster delivery wherever you need it.`,
    },
    {
      finalValue: 4,
      displayValue: undefined,
      title: 'Dumpster Options',
      description: `${companyName} offers 4 different dumpster sizes to perfectly match your residential and commercial waste needs.`,
    },
    {
      finalValue: 1000,
      displayValue: undefined,
      title: 'Satisfied Customers',
      description: `${companyName} has successfully completed over +1000 dumpster rentals, making us one of the most trusted waste management services.`,
    },
    {
      finalValue: googleData?.averageRating ?? 5,
      displayValue: undefined,
      title: 'Highly-rated service',
      description: `Our customers love our service, giving ${companyName} an impressive ${
        googleData?.averageRating ?? 5
      } star rating on Google reviews.`,
    },
  ];

  // Combine variant values with default stats
  const stats = useMemo(() => {
    if (!titles.length && !subtitles.length && !values.length) {
      return defaultStats;
    }

    const orderIndexes = [
      ...new Set([
        ...titles.map((t) => t.orderIndex ?? 0),
        ...subtitles.map((s) => s.orderIndex ?? 0),
        ...values.map((v) => v.orderIndex ?? 0),
      ]),
    ].sort((a, b) => a - b);

    return orderIndexes
      .map((index) => {
        const title = titles.find((t) => t.orderIndex === index)?.value;
        const subtitle = subtitles.find((s) => s.orderIndex === index)?.value;
        const value = values.find((v) => v.orderIndex === index)?.value;
        const defaultStat = defaultStats[index];

        // If we don't have a default stat for this index, skip it
        if (!defaultStat && (!title || !value)) {
          return null;
        }

        // Parse the value as a number, fallback to default if not a valid number
        let finalValue = defaultStat?.finalValue ?? 0;
        let displayValue: string | undefined;

        if (value) {
          const parsedValue = parseFloat(value);
          if (!isNaN(parsedValue)) {
            finalValue = parsedValue;
          }
          // If value contains non-numeric characters (like "50k+") or decimals, use it as display value
          if (/[a-zA-Z+]/.test(value) || (value.includes('.') && !Number.isInteger(parsedValue))) {
            displayValue = value;
          }
        }

        return {
          finalValue,
          displayValue,
          title: title ?? defaultStat?.title ?? '',
          description: subtitle ?? defaultStat?.description ?? '',
        };
      })
      .filter((stat): stat is Stat => stat !== null); // Filter out null values with type predicate
  }, [
    titles,
    subtitles,
    values,
    defaultStats,
    companyName,
    googleData?.averageRating,
  ]);

  // Animation states for each stat
  const [animatedValues, setAnimatedValues] = useState(stats.map(() => 0));
  const [hasAnimated, setHasAnimated] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3, // Trigger when 30% of the component is visible
    };

    // Handler for when the component comes into view
    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && !hasAnimated) {
        setHasAnimated(true);
        animateNumbers();
      }
    };

    // Create an observer to detect when component is in viewport
    const observer = new IntersectionObserver(handleIntersect, options);

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [hasAnimated]);

  const animateNumbers = () => {
    const duration = 2000;
    const startTime = Date.now();

    const updateNumbers = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const newValues = stats.map((stat) => {
        return Math.floor(progress * Number(stat.finalValue));
      });

      setAnimatedValues(newValues);

      if (progress < 1) {
        requestAnimationFrame(updateNumbers);
      }
    };

    requestAnimationFrame(updateNumbers);
  };

  const googleReviewsHidden =
    !googleData ||
    !googleData.reviews ||
    googleData.reviews.length === 0 ||
    (googleData.totalReviews && googleData.totalReviews < 10);

  return (
    <Container
      size="4"
      p="8"
      className={
        googleReviewsHidden || withBackground
          ? `relative min-h-[200px] overflow-hidden before:content-[''] before:absolute before:inset-0 before:bg-[url('/default/rental-steps-bg.svg')] before:bg-cover before:bg-center before:bg-no-repeat before:opacity-100 before:-z-10 after:content-[''] after:absolute after:inset-0 after:opacity-90 after:-z-10`
          : ''
      }
    >
      <Flex direction="column" gap="2" className="mb-8">
        <Text
          size="2"
          weight="bold"
          className="text-neutral-400"
          data-variable="COMPANY_STATS_SUBTITLE"
        >
          {statsSubtitle}
        </Text>
        <Heading
          as="h2"
          size="8"
          className="max-w-3xl"
          data-variable="COMPANY_STATS_TITLE"
        >
          {statsTitle}
        </Heading>
      </Flex>

      <Grid
        ref={containerRef}
        columns={{ initial: '1', sm: '2', lg: '4' }}
        gap="6"
      >
        {stats.map((stat, index) => (
          <Box key={index} className="flex h-[280px]">
            <Card direction="column" padding="8">
              <Flex direction="column" className="h-full">
                <Heading
                  as="h3"
                  size="9"
                  style={{ color: 'var(--primary-color)' }}
                  data-variable="STATS_VALUE"
                  data-order-index={index}
                >
                  {stat.displayValue
                    ? stat.displayValue
                    : hasAnimated
                    ? stat.finalValue === 1000
                      ? animatedValues[index].toLocaleString()
                      : animatedValues[index]
                    : '0'}
                </Heading>
                <Heading
                  as="h4"
                  size="4"
                  className="mt-3"
                  data-variable="STATS_TITLE"
                  data-order-index={index}
                >
                  {stat.title}
                </Heading>
                <Text
                  size="2"
                  className={`text-gray-600 mt-4 ${
                    index !== 2 ? 'line-clamp-4' : ''
                  }`}
                  data-variable="STATS_SUBTITLE"
                  data-order-index={index}
                >
                  {stat.description}
                </Text>
              </Flex>
            </Card>
          </Box>
        ))}
      </Grid>
    </Container>
  );
};

export default CompanyStats;
